import { Dispatch, SetStateAction } from "react";
import { Box, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { Typography } from "@appsflyer/fe-ui-core";
import { DJCodeBlock, DJSimpleCodeBlock } from "../../main/components/codeblock";
import { EvgenEventsObject } from "../IEvgen";
import { IOption } from "../SendInappEventParam";
import { SendInappStep } from "./types";

interface MultiSelectControlProps {
  options: IOption[];
  onSelectedEventsChange?: (selectedValues: IOption[]) => void;
}

interface LogEventStepParams {
  t: (key: string) => string;
  isFlutter: boolean;
  isReactNative: boolean;
  tabValue: number;
  responseListRequired: string;
  responseListenerRequired: boolean;
  setResponseListenerRequired: Dispatch<SetStateAction<boolean>>;
  handleResponseChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  inappCodeWListenerPrimary: string;
  inappCodeWListenerSecondary: string;
  evgenInappCodeJava: string;
  evgenInappCodeKotlin: string;
  evgenInappCodeFlutter: string;
  flutterLogEventCode: string;
  reactNativeStep3Code: string;
  evgenEventsObject?: EvgenEventsObject;
  filterEvgenEvents: (selectedValues: IOption[]) => void;
  MultiSelectControl: React.ComponentType<MultiSelectControlProps>;
  ExplanationTooltip: React.ComponentType;
}

export const createLogEventStep = ({
  t,
  isFlutter,
  isReactNative,
  tabValue,
  responseListRequired,
  responseListenerRequired,
  setResponseListenerRequired,
  handleResponseChange,
  inappCodeWListenerPrimary,
  inappCodeWListenerSecondary,
  evgenInappCodeJava,
  evgenInappCodeKotlin,
  evgenInappCodeFlutter,
  flutterLogEventCode,
  reactNativeStep3Code,
  evgenEventsObject,
  filterEvgenEvents,
  MultiSelectControl,
  ExplanationTooltip,
}: LogEventStepParams): SendInappStep => ({
  label: t("ip_AppEventInApp_android_stepFiveHeading"),
  component: (
    <>
      <Typography>{t("ip_AppEventInApp_android_logEventDescription")}</Typography>
      {isFlutter ? (
        <>
          {tabValue == 1 && (
            <DJSimpleCodeBlock
              codePrimary={flutterLogEventCode}
              showLineNumbers={false}
              language="dart"
            />
          )}
          {tabValue == 0 && (
            <>
              {evgenEventsObject?.events?.length && (
                <MultiSelectControl
                  options={evgenEventsObject.events.map((event) => ({
                    value: event.eventIdentifier,
                    label: event.eventName,
                  }))}
                  onSelectedEventsChange={(selectedValues) =>
                    filterEvgenEvents(selectedValues)
                  }
                />
              )}
              <DJSimpleCodeBlock
                codePrimary={evgenInappCodeFlutter}
                showLineNumbers={false}
                language="dart"
              />
            </>
          )}
        </>
      ) : (
        <>
          {!isReactNative && (
            <Box mt={2}>
              <Typography variant="body1">
                {t("ip_AppEventInApp_android_responseListenerLabel")}
                <ExplanationTooltip />
              </Typography>
              <RadioGroup row value={responseListRequired} onChange={handleResponseChange}>
                <FormControlLabel
                  label={t("ip_AppEventInApp_android_noLabel")}
                  control={<Radio checked={!responseListenerRequired} />}
                  onChange={() => setResponseListenerRequired(false)}
                  value="No"
                />
                <FormControlLabel
                  label={t("ip_AppEventInApp_android_yesLabel")}
                  control={<Radio checked={responseListenerRequired} />}
                  onChange={() => setResponseListenerRequired(true)}
                  value="Yes"
                />
              </RadioGroup>
            </Box>
          )}
          {isReactNative ? (
            <>
              {evgenEventsObject?.events?.length && (
                <MultiSelectControl
                  options={evgenEventsObject.events.map((event) => ({
                    value: event.eventIdentifier,
                    label: event.eventName,
                  }))}
                  onSelectedEventsChange={(selectedValues) =>
                    filterEvgenEvents(selectedValues)
                  }
                />
              )}
              <Box mt={2}>
                {tabValue == 1 ? (
                  <DJSimpleCodeBlock
                    codePrimary={reactNativeStep3Code}
                    showLineNumbers={true}
                    wrapLongLines={false}
                    language="javascript"
                  />
                ) : (
                  <DJSimpleCodeBlock
                    codePrimary={evgenInappCodeJava}
                    showLineNumbers={true}
                    wrapLongLines={false}
                    language="javascript"
                  />
                )}
              </Box>
            </>
          ) : (
            <>
              {tabValue == 1 && (
                <DJCodeBlock
                  codePrimary={inappCodeWListenerPrimary}
                  codeSecondary={inappCodeWListenerSecondary}
                  showLineNumbers={true}
                />
              )}
              {tabValue == 0 && (
                <>
                  {evgenEventsObject?.events?.length && (
                    <MultiSelectControl
                      options={evgenEventsObject.events.map((event) => ({
                        value: event.eventIdentifier,
                        label: event.eventName,
                      }))}
                      onSelectedEventsChange={(selectedValues) =>
                        filterEvgenEvents(selectedValues)
                      }
                    />
                  )}
                  <DJCodeBlock
                    codePrimary={evgenInappCodeJava}
                    codeSecondary={evgenInappCodeKotlin}
                    showLineNumbers={true}
                    wrapLongLines={false}
                  />
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  ),
  shouldDisplayNavigationButtons: true,
});
